/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { CultureID } from '../../../Localization/CultureID';
import LocalizationComponent from '../../../Localization/LocalizationComponent';
import ComponentBase from '../../ComponentBase';
import CLog from '../../ComponentLogger';
import TypeStrings from '../../TypeStrings';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import UpdateableLabelComponent from '../Observable/UpdateableLabelComponent';
import UpdateableResourceComponent, { TextTransformOption } from '../Observable/UpdateableResourceComponent';
import MutatorComponentBase from './MutatorComponentBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class LocalizationMutator extends MutatorComponentBase
{
    @property(cc.Node)
    protected topNode: cc.Node = null;

    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();
    private _valueKeyHash: object = {};

    @property
    public CleanStart: boolean = false;

    @property
    public get Localize(): boolean { return false; }
    public set Localize(value: boolean)
    {
        if (!this.topNode)
        {
            CLog.error('Top node not set.');
            return;
        }

        if (!this._localRef.Value)
        {
            CLog.error('No localization component.');
            return;
        }

        this.mapValues()
            .then(() =>
            {
                this.execute(this.topNode, [this.localizeComponentHandler.bind(this)]);
            });
    }

    /** @hidden */
    protected setContexts(): void
    {
        this._localRef.SetContext(LocalizationComponent);
    }

    private mapValues(): Promise<any>
    {
        return new Promise((resolve, reject) =>
        {
            this._valueKeyHash = new Map();
            this._localRef.Value
                .SetCulture(CultureID.en_us)
                .then((data: any) =>
                {
                    let keys: string[] = Object.keys(data);
                    for (let key of keys)
                    {
                        let fileResource: object = data[key];
                        if (!fileResource) { return; }

                        if (key === this._localRef.Value.DefaultResource)
                        {
                            key = '';
                        }
                        else
                        {
                            key =  `${key}:`;
                        }

                        this.mapValue(fileResource, key);
                    }
                    resolve(true);
                }).catch(() => reject(false));
        });
    }

    private mapValue(value: Object | string, path: string = ''): void
    {
        if (typeof(value) === TypeStrings.object)
        {
            let keys: string[] = Object.keys(value);
            for (let key of keys)
            {
                this.mapValue(value[key], path.endsWith(':')
                    ? `${path}${key}`
                    : (path ? `${path}/${key}` : key));
            }
            return;
        }

        let keyString: string = (value as string).toLowerCase();
        this._valueKeyHash[keyString] = path;
    }

    private localizeComponentHandler(target: cc.Node): boolean
    {
        let warn: (msg: string) => void = this.warn.bind(this, target);
        let error: (msg: string) => void = this.error.bind(this, target);

        let label: cc.Label = ComponentBase.GetFirstComponent<cc.Label>(target, cc.Label, cc.RichText);
        if (!label)
        {
            return false;
        }

        let value: string = label.string;
        if (!value)
        {
            return false;
        }

        let isUpperCase: boolean = value.toUpperCase() === value;
        let lowerCaseValue: string = value.toLocaleLowerCase();
        let trimLowerCaseValue: string = lowerCaseValue.trim();
        let localKey: string = this._valueKeyHash[trimLowerCaseValue];

        if (localKey)
        {
            let resourceComponent: UpdateableResourceComponent = target.getComponent(UpdateableResourceComponent);
            if (resourceComponent && this.CleanStart)
            {
                resourceComponent.destroy();
                resourceComponent = null;
            }
            
            if (!resourceComponent)
            {
                warn(`Resource found: [${localKey}].`);
                if (this.shouldModify)
                {
                    resourceComponent = target.addComponent(UpdateableResourceComponent);
                    resourceComponent.LocalKey = localKey;
                    if (label.string.toUpperCase() === label.string)
                    {
                        resourceComponent.TextTransformOption = TextTransformOption.Uppercase;
                    }
                    this.warn(target, 'Added UpdateableResourceComponent.');
                }
                return true;
            }

            if (value.toLocaleLowerCase() !== trimLowerCaseValue)
            {
                if (this.shouldModify)
                {
                    label.string = isUpperCase ? value.toUpperCase() : value.trim();
                    warn('Updated label string value.');
                }
                return true;
            }
        }
        else
        {
            if (target.getComponent(UpdateableLabelComponent)
                || !!Number.parseFloat(trimLowerCaseValue))
            {
                return false;
            }

            let resourceComp: UpdateableResourceComponent = target.getComponent(UpdateableResourceComponent);
            if (resourceComp)
            {
                warn(`Label has key: [${resourceComp.LocalKey}], but value mismatched.`);
                if (this.shouldModify)
                {
                    label.string = value;
                    warn(`Label value set to [${value}].`);
                }
            }
            else
            {
                error(`Label not localized and no matching resource found, value key: [${trimLowerCaseValue}].`);
            }

            return true;
        }
    }
}
